import {
  Button,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { FiDownload } from "react-icons/fi";

// Lowercase file extension, ignoring any query string / hash.
// e.g. "https://host/file.PDF?token=x" -> "pdf"
const getExtension = (u = "") =>
  u.split(/[#?]/)[0].split(".").pop().trim().toLowerCase();

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif"];
const VIDEO_EXTS = ["mp4", "webm", "ogg", "mov", "m4v"];
// Office documents that the browser can't render natively but Microsoft's
// Office Online viewer can.
const OFFICE_EXTS = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];

const ImageModal = React.memo(({ url = "", isOpen, onClose, vid = false }) => {
  const ext = getExtension(url);
  const isVideo = vid || VIDEO_EXTS.includes(ext);
  const isImage = IMAGE_EXTS.includes(ext);
  const isOffice = OFFICE_EXTS.includes(ext);
  // Anything that isn't a video or image is treated as a document.
  const isDocument = !isVideo && !isImage;

  // Office files (doc/xls/ppt) have no native browser renderer, so route those
  // through Microsoft's Office Online viewer (more reliable than Google's
  // deprecated `gview`). Requires a public URL.
  const isOfficeDoc = isDocument && isOffice;
  const officeViewerSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
    url
  )}`;

  // For PDFs (and other browser-renderable docs) we can't just point an iframe
  // at the raw URL: if the file server sends `X-Frame-Options` or
  // `Content-Disposition: attachment`, the browser refuses to render it inline
  // and the pane goes blank even though the URL opens fine in its own tab.
  // Fetching the file and rendering it as a blob URL sidesteps both — the blob
  // is same-origin to the iframe, so framing/disposition headers no longer
  // apply.
  const needsBlob = isDocument && !isOffice;

  const [blobUrl, setBlobUrl] = React.useState("");
  const [loadError, setLoadError] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen || !needsBlob || !url) return;

    let objectUrl = "";
    let cancelled = false;
    setBlobUrl("");
    setLoadError(false);

    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        // Force the PDF content type so the browser renders it inline even if
        // the server mislabels it (e.g. application/octet-stream).
        const type = ext === "pdf" ? "application/pdf" : res.headers.get("content-type") || "";
        const blob = new Blob([buf], type ? { type } : undefined);
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setBlobUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setLoadError(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isOpen, needsBlob, url, ext]);

  // Download the file via a blob so it saves instead of navigating, keeping the
  // original filename from the URL.
  const downloadFile = async () => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const dlUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = dlUrl;
      link.download = url.split(/[#?]/)[0].split("/").pop() || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      // Fall back to opening the file if the blob download is blocked.
      window.open(url, "_blank", "noopener");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxW={"container.xl"} h={"90vh"}>
        <ModalCloseButton zIndex={100} bg={"#99999988"} />
        <ModalHeader py={2}>
          <Flex justify={"end"} mr={10}>
            <Button
              size={"sm"}
              colorScheme={"yellow"}
              leftIcon={<FiDownload />}
              onClick={downloadFile}
            >
              Download
            </Button>
          </Flex>
        </ModalHeader>
        <ModalBody p={0} bg={"cardBg"} borderRadius={8} overflow={"hidden"}>
          {isVideo ? (
            <video
              src={url}
              controls
              autoPlay
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            >
              Your browser does not support the video tag.
            </video>
          ) : isImage ? (
            <Flex w={"full"} h={"full"} align={"center"} justify={"center"}>
              <Image src={url} maxW={"100%"} maxH={"100%"} objectFit={"contain"} />
            </Flex>
          ) : isOfficeDoc ? (
            <iframe
              title={"document-viewer"}
              src={officeViewerSrc}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            isDocument && (
              <Flex w={"full"} h={"full"} align={"center"} justify={"center"}>
                {loadError ? (
                  <Flex direction={"column"} align={"center"} gap={3}>
                    <Text color={"red.500"}>
                      Couldn't load a preview for this file.
                    </Text>
                    <Button
                      size={"sm"}
                      colorScheme={"yellow"}
                      onClick={() => window.open(url, "_blank", "noopener")}
                    >
                      Open in new tab
                    </Button>
                  </Flex>
                ) : blobUrl ? (
                  <iframe
                    title={"document-viewer"}
                    src={blobUrl}
                    style={{ width: "100%", height: "100%", border: "none" }}
                  />
                ) : (
                  <Spinner size={"xl"} color={"yellow.400"} />
                )}
              </Flex>
            )
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});

export default ImageModal;
